import { useCallback, useRef, useState } from 'react';
import { HttpStatusCode } from 'axios';
import { improveText_API } from '../../api/controller/AIChatController';

export const AI_IMPROVEMENT_PROMPTS = [
    'Make it more concise and to the point.',
    'Make it more professional and formal.',
    'Simplify the language for better understanding.',
    'Expand with more details and clarity.',
    'Use more technical and precise language.',
    'Fix grammar and improve readability.',
];

/**
 * Normalizes raw axios response / error into { ok, improvedText, message, fields }.
 */
const unwrapImproveResponse = (result) => {
    if (
        result?.status === HttpStatusCode.Ok ||
        result?.status === HttpStatusCode.Created
    ) {
        const improvedText =
            result?.data?.improvedText ??
            result?.data?.data?.improvedText ??
            null;

        return {
            ok: !!improvedText,
            improvedText,
            message: result?.data?.message || 'Text improved successfully.',
        };
    }

    const status = result?.response?.status;
    const data = result?.response?.data;

    if (status === HttpStatusCode.NotAcceptable) {
        return {
            ok: false,
            improvedText: null,
            message: data?.message || 'Validation error.',
            fields: data?.fields,
        };
    }

    if (result?.request && !result?.response) {
        return {
            ok: false,
            improvedText: null,
            message:
                'Network error — AI service is unreachable. Please check your connection.',
        };
    }

    return {
        ok: false,
        improvedText: null,
        message:
            data?.message ||
            result?.message ||
            'Failed to improve text. Please try again.',
    };
};

/**
 * AI text improvement hook — SUGGESTION MODE.
 *
 * The hook NEVER mutates the parent text directly.
 * It stores the AI result in `pendingSuggestion` and waits for the user to
 * Accept or Revert via `acceptSuggestion()` / `revertSuggestion()`.
 */
export default function useAiImproveText({ onSuggestion, onApplied, onError } = {}) {
    const [isImproving, setIsImproving] = useState(false);
    const [improvementCount, setImprovementCount] = useState(0);
    const [aiError, setAiError] = useState(null);

    // The AI suggestion waiting for user action
    const [pendingSuggestion, setPendingSuggestion] = useState(null);
    // The original text snapshot when the first suggestion was made
    const originalTextRef = useRef('');

    const reset = useCallback(() => {
        setIsImproving(false);
        setImprovementCount(0);
        setAiError(null);
        setPendingSuggestion(null);
        originalTextRef.current = '';
    }, []);

    /**
     * Fetch an AI suggestion. Does NOT apply it — stores in pendingSuggestion.
     * @param {string} currentText
     * @param {string} [overridePrompt]
     */
    const improve = useCallback(
        async (currentText, overridePrompt) => {
            const text = (currentText || '').trim();

            if (!text) {
                const msg = 'Please enter some description to improve.';
                setAiError(msg);
                onError?.(msg);
                return { success: false, error: msg };
            }

            // Snapshot original only on first suggestion
            if (!originalTextRef.current) {
                originalTextRef.current = currentText;
            }

            setIsImproving(true);
            setAiError(null);

            const promptIndex = improvementCount % AI_IMPROVEMENT_PROMPTS.length;
            const prompt = overridePrompt || AI_IMPROVEMENT_PROMPTS[promptIndex];

            try {
                const raw = await improveText_API({ text, userPrompt: prompt });
                const { ok, improvedText, message, fields } =
                    unwrapImproveResponse(raw);

                if (ok && improvedText) {
                    setPendingSuggestion({
                        text: improvedText,
                        sourceText: currentText,
                        promptIndex,
                        prompt,
                    });
                    onSuggestion?.(improvedText, { promptIndex, prompt });
                    return { success: true, improvedText };
                }

                const errorMsg =
                    message || 'Failed to improve text. Please try again.';
                setAiError(errorMsg);
                onError?.(errorMsg, { fields });
                return { success: false, error: errorMsg, fields };
            } catch (err) {
                const errorMsg =
                    err?.message || 'Failed to improve text. Please try again.';
                setAiError(errorMsg);
                onError?.(errorMsg);
                return { success: false, error: errorMsg };
            } finally {
                setIsImproving(false);
            }
        },
        [improvementCount, onSuggestion, onError]
    );

    /**
     * User accepted the suggestion → applyText receives the new text.
     */
    const acceptSuggestion = useCallback(
        (applyText) => {
            if (!pendingSuggestion) return false;
            applyText(pendingSuggestion.text);
            setImprovementCount((c) => c + 1);
            onApplied?.(pendingSuggestion.text, {
                promptIndex: pendingSuggestion.promptIndex,
                prompt: pendingSuggestion.prompt,
            });
            setPendingSuggestion(null);
            return true;
        },
        [pendingSuggestion, onApplied]
    );

    /**
     * User reverted → discard suggestion. Original text untouched.
     */
    const revertSuggestion = useCallback(() => {
        setPendingSuggestion(null);
    }, []);

    const copyToClipboard = useCallback(async (text) => {
        try {
            await navigator.clipboard.writeText(text || '');
            return true;
        } catch {
            return false;
        }
    }, []);

    return {
        // state
        isImproving,
        improvementCount,
        aiError,
        setAiError,
        pendingSuggestion,
        hasPendingSuggestion: !!pendingSuggestion,
        originalText: originalTextRef.current,
        // actions
        improve,
        acceptSuggestion,
        revertSuggestion,
        reset,
        copyToClipboard,
        totalPrompts: AI_IMPROVEMENT_PROMPTS.length,
    };
}